import { FormEvent, KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  errorMessage,
  fetchCollectionInsights,
  fetchCollectionTargets,
  suggestMarketAddresses,
  type AddressSuggestion,
  type CollectionInsightIcon,
} from './api';
import { ZoneVisual } from './MarketVisuals';
import { RadarEyeVisual } from './InsightVisuals';

function HomeIcon({ name }: { name: 'competitor' | 'opportunity' | CollectionInsightIcon }) {
  const paths: Record<typeof name, ReactNode> = {
    competitor: <><circle cx="9" cy="9" r="3"/><path d="M3.5 19c.7-3.2 2.5-4.8 5.5-4.8s4.8 1.6 5.5 4.8M15 7.5a3 3 0 0 1 0 5.8M16.5 15c2.1.4 3.4 1.7 4 4"/></>,
    opportunity: <><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/></>,
    aperture: <><path d="M5 19h14M7 19V8h10v11M5 8l7-5 7 5"/><path d="M12 10v6m-3-3h6"/></>,
    chiusure: <><circle cx="12" cy="12" r="8"/><path d="m9 9 6 6m0-6-6 6"/></>,
    crescita: <><path d="m4 17 5-5 4 3 7-8"/><path d="M15 7h5v5"/></>,
    registro: <><path d="M4 21h16M6 21V9h12v12M4 9l8-6 8 6"/><path d="M9 13h2m2 0h2m-6 4h2m2 0h2"/></>,
    bisogno: <><path d="M9 18h6M10 22h4"/><path d="M8.2 14.8A7 7 0 1 1 15.8 14.8c-.8.7-1.3 1.5-1.4 2.2H9.6c-.1-.7-.6-1.5-1.4-2.2Z"/></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

export default function Home() {
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [picked, setPicked] = useState(false);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const insights = useQuery({ queryKey: ['collection-insights', 6], queryFn: () => fetchCollectionInsights(6) });
  const targets = useQuery({ queryKey: ['collection-targets'], queryFn: fetchCollectionTargets });
  const suggestions = useQuery({
    queryKey: ['home-address-suggestions', debounced],
    queryFn: ({ signal }) => suggestMarketAddresses(debounced, 6, signal),
    enabled: debounced.length >= 3,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const normalized = address.trim();
    if (picked || normalized.length < 3) {
      setDebounced('');
      return;
    }
    const timer = window.setTimeout(() => setDebounced(normalized), 300);
    return () => window.clearTimeout(timer);
  }, [address, picked]);

  useEffect(() => setActiveIndex(-1), [suggestions.data]);

  const latestZone = (targets.data ?? []).find((target) => target.kind === 'zona' && target.active)
    ?? (targets.data ?? []).find((target) => target.kind === 'zona');
  const options = suggestions.data ?? [];
  const showSuggestions = open && !picked && address.trim().length >= 3 && debounced === address.trim();

  function chooseAddress(suggestion: AddressSuggestion) {
    setAddress(suggestion.label);
    setPicked(true);
    setOpen(false);
  }

  function start(intent: 'competitors' | 'opportunity') {
    const params = new URLSearchParams({ new: '1', mode: 'local', intent });
    if (address.trim()) params.set('zone', address.trim());
    navigate(`/competitor?${params.toString()}`);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (address.trim().length >= 3) start('competitors');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!showSuggestions) return;
    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp' && options.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      chooseAddress(options[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return <div className="home-page">
    <header className="home-hero">
      <p className="eyebrow">Market intelligence locale</p>
      <h1>Osserva.<br/>Confronta.<br/><span>Decidi meglio.</span></h1>
      <p>Scopri chi sono i tuoi concorrenti e quali attività hanno più potenziale nella tua zona.</p>
      <div className="home-radar-art" aria-hidden="true"><RadarEyeVisual/><span><i/>Il mercato<br/>sotto controllo</span></div>
      <form className="home-address" onSubmit={submit} role="search">
        <span aria-hidden="true"><HomeIcon name="opportunity"/></span>
        <input
          type="search"
          value={address}
          onChange={(event) => { setAddress(event.target.value); setPicked(false); setOpen(true); }}
          onFocus={() => { if (!picked) setOpen(true); }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder="Inserisci un indirizzo o seleziona un’area"
          aria-label="Indirizzo o area da analizzare"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls={showSuggestions ? 'home-address-suggestions' : undefined}
          autoComplete="off"
        />
        <button type="submit" aria-label="Continua con questo indirizzo" disabled={address.trim().length < 3}>→</button>
        {showSuggestions && <div className="home-address-menu" id="home-address-suggestions">
          {suggestions.isFetching ? <p role="status">Cerco indirizzi…</p>
            : suggestions.isError ? <p>Suggerimenti non disponibili. Puoi continuare con il testo inserito.</p>
              : options.length === 0 ? <p>Nessun indirizzo trovato.</p>
                : <ul role="listbox">{options.map((option, index) => <li key={`${option.label}-${index}`} role="none"><button type="button" role="option" aria-selected={activeIndex === index} className={activeIndex === index ? 'is-active' : undefined} onMouseDown={(event) => event.preventDefault()} onClick={() => chooseAddress(option)} onMouseEnter={() => setActiveIndex(index)}><strong>{option.main || option.label}</strong>{option.secondary && <small>{option.secondary}</small>}</button></li>)}</ul>}
        </div>}
      </form>
    </header>

    <section className="home-actions" aria-label="Azioni principali">
      <button type="button" onClick={() => start('competitors')}>
        <span><HomeIcon name="competitor"/></span><strong>Analizza i concorrenti</strong><small>Scopri chi sono e quanto spingono nella zona.</small><b><em>Inizia analisi</em><i aria-hidden="true">→</i></b>
      </button>
      <button type="button" onClick={() => start('opportunity')}>
        <span><HomeIcon name="opportunity"/></span><strong>Trova opportunità</strong><small>Individua gli spazi con più potenziale di crescita.</small><b><em>Esplora zone</em><i aria-hidden="true">→</i></b>
      </button>
    </section>

    <section className="home-insights" aria-labelledby="home-insights-title">
      <header><div><p className="eyebrow">Dati raccolti</p><h2 id="home-insights-title">Ultimi insight</h2></div><Link to="/monitoraggi?tab=raccolta">Vedi raccolta</Link></header>
      {insights.isPending && <div className="home-insight-skeleton" role="status"><span className="sr-only">Caricamento insight</span><i/><i/><i/></div>}
      {insights.isError && <div className="notice notice-error"><p>{errorMessage(insights.error)}</p><button className="btn-secondary" type="button" onClick={() => insights.refetch()}>Riprova</button></div>}
      {insights.isSuccess && insights.data.length === 0 && <div className="home-insights-empty"><HomeIcon name="crescita"/><div><strong>Gli insight compaiono man mano che la raccolta lavora</strong><p>Aggiungi una zona in Attività per iniziare a osservare aperture, chiusure e segnali di crescita.</p></div></div>}
      {insights.isSuccess && insights.data.length > 0 && <ol>{insights.data.map((insight) => <li key={`${insight.targetId}-${insight.icon}-${insight.title}`}><Link to={`/storico/${encodeURIComponent(insight.targetId)}`}><span><HomeIcon name={insight.icon}/></span><div><strong>{insight.title}</strong><small>{insight.detail}</small></div><b className={`is-${insight.level}`} aria-label={`Livello ${insight.level}`}>{insight.level === 'alto' ? '↗' : insight.level === 'basso' ? '↘' : '→'}</b></Link></li>)}</ol>}
    </section>

    <section className="home-map-preview" aria-labelledby="home-map-title">
      <header><div><p className="eyebrow">Zona osservata</p><h2 id="home-map-title">Il mercato, a colpo d’occhio</h2></div></header>
      <div className="home-map-art"><ZoneVisual compact radiusLabel={latestZone?.config.radiusMeters ? `${latestZone.config.radiusMeters / 1_000} km` : '2 km'} title={latestZone?.label ?? 'Nessuna zona selezionata'} markers={latestZone ? 6 : 0}/><Link className="home-map-open" to={latestZone ? `/mappa?target=${encodeURIComponent(latestZone.id)}` : '/mappa'}>Apri mappa <span aria-hidden="true">↗</span></Link></div>
      <div className="home-map-actions">
        {latestZone ? <Link className="btn-secondary" to={`/storico/${encodeURIComponent(latestZone.id)}`}>Nel tempo <span aria-hidden="true">→</span></Link> : <span className="btn-secondary is-disabled" aria-disabled="true">Nel tempo <span aria-hidden="true">→</span></span>}
      </div>
    </section>
  </div>;
}
